import { FC, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { runtimeConfig } from "src/services/runtimeConfig";

import {
  Grid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import ReactDOM from "react-dom";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";

import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { registerDateChangeCallback } from "./helper/dateFilterCallbackStore";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Window } from "@progress/kendo-react-dialogs";

type AccountPaymentDTO = {
  acctId: string;
  accountName: string;
  paymentNum: string;
  invoiceId: number;
  paymentAmount: number;
  notes: string;
  receivedOn: Date;
  paymentAccount: string;
  paymentAccountLocation: string;
};

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: any) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};
interface PageState {
  skip: number;
  take: number;
}

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};
const initialPage: PageState = { skip: 0, take: 20 };

// Helper function to convert data to CSV format (Excel-compatible)
const convertToCSV = (data: AccountPaymentDTO[]): string => {
  if (!data || data.length === 0) return "";

  // Define the headers
  const headers = [
    "Account ID",
    "Account Name",
    "Payment Num",
    "Invoice ID",
    "Payment Amount",
    "Notes",
    "Received On",
    "Payment Account",
    "Payment Account Location",
  ];

  // Convert data to CSV rows with proper escaping
  const csvRows = data.map((item) => [
    item.acctId || "",
    `"${(item.accountName || "").replace(/"/g, '""')}"`, // Escape quotes in account name
    item.paymentNum || "",
    item.invoiceId || "",
    item.paymentAmount || "",
    `"${(item.notes || "").replace(/"/g, '""')}"`, // Escape quotes in notes
    item.receivedOn ? item.receivedOn.toLocaleDateString() : "",
    `"${(item.paymentAccount || "").replace(/"/g, '""')}"`, // Escape quotes in payment account
    `"${(item.paymentAccountLocation || "").replace(/"/g, '""')}"`, // Escape quotes in payment account location
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(","),
    ...csvRows.map((row) => row.join(",")),
  ].join("\n");

  return csvContent;
};

const Payments: FC = () => {
  const [accountPayments, setAccountPayments] = useState<AccountPaymentDTO[]>(
    [],
  );
  const [showLoading, setShowLoading] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const gridRef = useRef(null);
  const [filter, setFilter] =
    useState<CompositeFilterDescriptor>(initialFilter);
  const [page, setPage] = useState<PageState>(initialPage);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null,
  );

  // Date range state - default to one year back
  const [startDate, setStartDate] = useState<Date>(() => {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return oneYearAgo;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());

  const loadData = async () => {
    setShowLoading(true);
    try {
      const response = await callApi(
        "sysadmin/payment/GetPayments",
        HttpMethod.POST,
        {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      );
      // Ensure receivedOn is properly formatted as Date objects and add unique ID
      const formattedData = response.data.map((item: any, index: number) => ({
        ...item,
        receivedOn: item.receivedOn ? new Date(item.receivedOn) : null,
        uniqueId: `${item.acctId}-${item.paymentNum}-${item.invoiceId}-${index}`,
      }));
      setAccountPayments(formattedData);
      setShowLoading(false);
    } catch (error) {
      console.error("Error fetching GetPayments:", error);
      alert(`Error fetching data from GetPayments: ${error}`);
      setShowLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [startDate, endDate]);

  // Modal handlers
  const handleInvoiceClick = useCallback((invoiceId: number) => {
    setSelectedInvoiceId(invoiceId);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedInvoiceId(null);
  }, []);

  useEffect(() => {
    registerDateChangeCallback(({ filterType, startDate, endDate }) => {
      console.log("Filter from child:", filterType, startDate, endDate);
      if (filterType == "custom") {
        const customFilter: CompositeFilterDescriptor = {
          logic: "and",
          filters: [
            { field: "receivedOn", operator: "gte", value: startDate },
            { field: "receivedOn", operator: "lte", value: endDate },
          ],
        };
        setFilter(customFilter);
      }
    });
  }, []);

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setIsFiltering(true);
    setFilter(e.filter);
    // Simulate filtering delay and then hide loader
    setTimeout(() => {
      setIsFiltering(false);
    }, 100);
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? accountPayments.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const AcctIdCell: FC<any> = (props) => {
    const acctId = props.dataItem[props.field];
    const targetUrl = `${runtimeConfig.classicUrl}/app/sysadmin/accounts.adp?acctId=${acctId}`;

    return (
      <td>
        <a
          href={targetUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#007bff",
            textDecoration: "underline",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          {acctId}
        </a>
      </td>
    );
  };

  // Apply client-side filtering
  const filteredData = useMemo(() => {
    if (!filter || !filter.filters || filter.filters.length === 0) {
      return accountPayments;
    }

    try {
      // Process the filter to handle date-only filtering for receivedOn field
      const processedFilter = {
        ...filter,
        filters: filter.filters.map((f: any) => {
          if (f.field === "receivedOn" && f.operator === "eq") {
            // For exact date match, convert to date range to exclude time
            const filterDate = new Date(f.value);
            const startOfDay = new Date(
              filterDate.getFullYear(),
              filterDate.getMonth(),
              filterDate.getDate(),
            );
            const endOfDay = new Date(
              filterDate.getFullYear(),
              filterDate.getMonth(),
              filterDate.getDate(),
              23,
              59,
              59,
              999,
            );

            return {
              logic: "and",
              filters: [
                { field: "receivedOn", operator: "gte", value: startOfDay },
                { field: "receivedOn", operator: "lte", value: endOfDay },
              ],
            };
          }
          return f;
        }),
      };

      return filterBy(accountPayments, processedFilter);
    } catch (error) {
      console.error("Filter error:", error);
      return accountPayments;
    }
  }, [accountPayments, filter]);

  const handleExportToExcel = useCallback(() => {
    // Convert data to CSV format (Excel-compatible)
    const csvContent = convertToCSV(filteredData);

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `payments-export-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredData]);

  const pagedData = useMemo(() => {
    const state: {
      skip: number;
      take: number;
    } = {
      skip: page.skip ?? 0,
      take: page.take ?? 20,
    };

    return filteredData.slice(state.skip, state.skip + state.take);
  }, [filteredData, page]);

  const PaymentAmountCell: FC<any> = (props) => {
    const amount = props.dataItem[props.field];
    const formattedAmount = amount
      ? `$${parseFloat(amount).toFixed(2)}`
      : "$0.00";

    return <td>{formattedAmount}</td>;
  };

  const InvoiceIdCell: FC<any> = useCallback(
    (props) => {
      const invoiceId = props.dataItem[props.field];

      return (
        <td>
          <button
            onClick={() => handleInvoiceClick(invoiceId)}
            style={{
              color: "#007bff",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: "bold",
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
            }}
          >
            {invoiceId}
          </button>
        </td>
      );
    },
    [handleInvoiceClick],
  );

  return (
    <>
      {/*{filter && (
        <div className="alert alert-info">
          Filter for debugging: {JSON.stringify(filter)}
        </div>
      )}*/}

      {/* Date Range Selector */}
      <div
        style={{
          padding: "10px",
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <label style={{ fontWeight: "bold", minWidth: "80px" }}>
              From Date:
            </label>
            <DatePicker
              value={startDate}
              onChange={(e) => setStartDate(e.value || new Date())}
              format="MM/dd/yyyy"
              style={{ width: "150px" }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <label style={{ fontWeight: "bold", minWidth: "80px" }}>
              To Date:
            </label>
            <DatePicker
              value={endDate}
              onChange={(e) => setEndDate(e.value || new Date())}
              format="MM/dd/yyyy"
              style={{ width: "150px" }}
            />
          </div>
          <div style={{ fontSize: "12px", color: "#6c757d" }}>
            Showing payments from {startDate.toLocaleDateString()} to{" "}
            {endDate.toLocaleDateString()}
          </div>
          <div style={{ marginLeft: "auto" }}>
            <button
              onClick={handleExportToExcel}
              className="btn btn-outline-secondary btn-sm d-flex align-items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-filetype-xlsx"
                viewBox="0 0 16 16"
              >
                <path
                  fill-rule="evenodd"
                  d="M14 4.5V11h-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM7.86 14.841a1.13 1.13 0 0 0 .401.823q.195.162.479.252.284.091.665.091.507 0 .858-.158.355-.158.54-.44a1.17 1.17 0 0 0 .187-.656q0-.336-.135-.56a1 1 0 0 0-.375-.357 2 2 0 0 0-.565-.21l-.621-.144a1 1 0 0 1-.405-.176.37.37 0 0 1-.143-.299q0-.234.184-.384.188-.152.513-.152.214 0 .37.068a.6.6 0 0 1 .245.181.56.56 0 0 1 .12.258h.75a1.1 1.1 0 0 0-.199-.566 1.2 1.2 0 0 0-.5-.41 1.8 1.8 0 0 0-.78-.152q-.44 0-.777.15-.336.149-.527.421-.19.273-.19.639 0 .302.123.524t.351.367q.229.143.54.213l.618.144q.31.073.462.193a.39.39 0 0 1 .153.326.5.5 0 0 1-.085.29.56.56 0 0 1-.255.193q-.168.07-.413.07-.176 0-.32-.04a.8.8 0 0 1-.249-.115.58.58 0 0 1-.255-.384zm-3.726-2.909h.893l-1.274 2.007 1.254 1.992h-.908l-.85-1.415h-.035l-.853 1.415H1.5l1.24-2.016-1.228-1.983h.931l.832 1.438h.036zm1.923 3.325h1.697v.674H5.266v-3.999h.791zm7.636-3.325h.893l-1.274 2.007 1.254 1.992h-.908l-.85-1.415h-.035l-.853 1.415h-.861l1.24-2.016-1.228-1.983h.931l.832 1.438h.036z"
                />
              </svg>
              Export to CSV
            </button>
          </div>
        </div>
      </div>

      <div ref={gridRef} style={{ padding: "5px" }}>
        {showLoading || isFiltering ? <LoadingPanel gridRef={gridRef} /> : null}
        <Grid
          className="sysadmin-grid"
          data={pagedData}
          dataItemKey="uniqueId"
          filterable={true}
          sortable={true}
          skip={page.skip}
          take={page.take}
          total={filteredData.length}
          pageable={{
            buttonCount: 4,
            pageSizes: [10, 20, 30, 40],
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          filter={filter}
          onFilterChange={handleFilterChange}
          style={{ height: "800px" }}
        >
          <Column
            field="acctId"
            title="Account ID"
            cell={AcctIdCell}
            filter="text"
          />
          <Column field="accountName" title="Account Name" filter="text" />
          <Column field="paymentNum" title="Payment Num" filter="text" />
          <Column
            field="invoiceId"
            title="InvoiceId"
            filter="numeric"
            cell={InvoiceIdCell}
          />
          <Column
            field="paymentAmount"
            title="Payment Amount"
            filter="numeric"
            cell={PaymentAmountCell}
          />
          <Column field="notes" width="580px" title="Notes" filter="text" />
          <Column
            field="receivedOn"
            title="Received On"
            width="220px"
            filter="date"
            format="{0:M/d/yyyy}"
          />
          <Column
            field="paymentAccount"
            width="180px"
            title="Payment Account"
            filter="text"
          />
          <Column
            field="paymentAccountLocation"
            title="Payment Account Location"
            filter="text"
          />
        </Grid>
      </div>

      {/* Invoice Details Modal */}
      {isModalOpen && selectedInvoiceId && (
        <Window
          title={`Invoice Details - #${selectedInvoiceId}`}
          onClose={handleModalClose}
          height={700}
          width={900}
          resizable={true}
          draggable={true}
        >
          <div
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <iframe
              src={`${runtimeConfig.classicUrl}/app/admin/b-invoice.adp?id=${selectedInvoiceId}&fromSysadmin=1&debug=1`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                flex: 1,
              }}
              title={`Invoice ${selectedInvoiceId}`}
            />
          </div>
        </Window>
      )}
    </>
  );
};

export default Payments;
